import React, { useState, useEffect, ReactNode, createContext, useContext, useCallback } from 'react';
import { getOrderStatusEmailTemplate } from '@/emails/orderStatusTemplates';
import { Save, Eye, RefreshCw, CheckCircle, Layout, Type, Image, List } from 'lucide-react';
import { STORAGE_KEY, getAllTemplates, saveTemplate, deleteTemplate } from '@/lib/emailTemplateUtils';
import { supabase } from '@/lib/supabaseClient';
import { toast } from 'react-hot-toast';

// Create a context for managing active tabs
const TabsContext = createContext<{
  activeTab: string;
  setActiveTab: React.Dispatch<React.SetStateAction<string>>;
}>({
  activeTab: 'editor',
  setActiveTab: () => {},
});

// Hook to use the tabs context
const useTabsContext = () => useContext(TabsContext);

// TabsProvider component for managing active tabs
const TabsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [activeTab, setActiveTab] = useState<string>('editor');
  
  return (
    <TabsContext.Provider value={{ activeTab, setActiveTab }}>
      {children}
    </TabsContext.Provider>
  );
};

// Custom tab components with TypeScript types
interface TabsProps {
  defaultValue: string;
  children: ReactNode;
}

const Tabs: React.FC<TabsProps> = ({ defaultValue, children }) => {
  const [activeTab, setActiveTab] = useState(defaultValue);
  
  return (
    <TabsContext.Provider value={{ activeTab, setActiveTab }}>
      <div>{children}</div>
    </TabsContext.Provider>
  );
};

interface TabsListProps {
  children: ReactNode;
  className?: string;
}

const TabsList: React.FC<TabsListProps> = ({ children, className }) => {
  return (
    <div className={`flex border-b ${className || ''}`}>
      {children}
    </div>
  );
};

interface TabsTriggerProps {
  value: string;
  children: ReactNode;
}

const TabsTrigger: React.FC<TabsTriggerProps> = ({ value, children }) => {
  const context = useContext(TabsContext);
  if (!context) {
    throw new Error('TabsTrigger must be used within a Tabs component');
  }
  
  const { activeTab, setActiveTab } = context;
  const isActive = activeTab === value;
  
  return (
    <button
      className={`px-4 py-2 font-medium ${isActive ? 'border-b-2 border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'}`}
      onClick={() => setActiveTab(value)}
    >
      {children}
    </button>
  );
};

interface TabsContentProps {
  value: string;
  children: ReactNode;
  className?: string;
}

const TabsContent: React.FC<TabsContentProps> = ({ value, children, className }) => {
  const context = useContext(TabsContext);
  if (!context) {
    throw new Error('TabsContent must be used within a Tabs component');
  }
  
  const { activeTab } = context;
  if (activeTab !== value) return null;
  
  return (
    <div className={className || ''}>
      {children}
    </div>
  );
};

// Sample order data for previewing templates
const SAMPLE_ORDER = {
  id: '12345-exempelorder',
  created_at: new Date().toISOString(),
  status: 'bekräftad',
  total_amount: 1250,
  delivery_date: new Date().toISOString().split('T')[0],
  delivery_time: '14:00-16:00',
  delivery_notes: 'Vänligen lämna vid dörren',
  address: 'Storgatan 1, 123 45 Stockholm',
  guest_email: 'kund@exempel.se',
  guest_details: {
    firstName: 'Test',
    lastName: 'Kund'
  },
  profile: {
    id: 'profile-123',
    first_name: 'Test',
    last_name: 'Kund',
    email: 'kund@exempel.se'
  },
  order_items: [
    {
      id: 'item-1',
      product: {
        id: 'product-1',
        name: 'Ekologiska äpplen',
        price: 25,
      },
      quantity: 4,
      unit_price: 25
    },
    {
      id: 'item-2',
      product: {
        id: 'product-2',
        name: 'Lokal honung',
        price: 89,
      },
      quantity: 1,
      unit_price: 89
    },
    {
      id: 'item-3',
      product: {
        id: 'product-3',
        name: 'Hantverksbröd',
        price: 45,
      },
      quantity: 2,
      unit_price: 45
    }
  ]
};

// Template types
const TEMPLATE_TYPES = [
  { id: 'pending', name: 'Väntande/Ny beställning', description: 'Skickas när en ny beställning tas emot' },
  { id: 'confirmed', name: 'Bekräftad', description: 'Skickas när en beställning bekräftas' },
  { id: 'preparing', name: 'Förbereds', description: 'Skickas när en beställning förbereds' },
  { id: 'out_for_delivery', name: 'Ute för leverans', description: 'Skickas när en beställning är ute för leverans' },
  { id: 'delivered', name: 'Levererad', description: 'Skickas när en beställning har levererats' },
  { id: 'cancelled', name: 'Avbruten', description: 'Skickas när en beställning avbryts' },
  { id: 'packed', name: 'Packad', description: 'Äldre - skickas när en beställning packas' }
];

// Template component interface for structured editing
interface TemplateComponents {
  enabled: boolean;  // Whether this email template is enabled for sending
  subject: string;
  greeting: string;
  intro: string;
  orderDetails: boolean;
  message: string;
  callToAction: string;
  callToActionUrl: string;
  signature: string;
  footerText: string;
  accentColor: string;
}

// Default values for a new template
const defaultTemplateComponents: TemplateComponents = {
  enabled: true,  // Enabled by default
  subject: 'Din beställning har [status]',
  greeting: 'Hej {{customer_name}},',
  intro: 'Tack för din beställning. Här är en uppdatering om din beställningsstatus.',
  orderDetails: true,
  message: 'Vi bearbetar för närvarande din beställning och kommer att uppdatera dig om nästa steg.',
  callToAction: 'Visa beställning',
  callToActionUrl: 'https://example.com/orders/{{order_id}}',
  signature: 'Lokalaproducenter-teamet',
  footerText: '© Lokalaproducenter. Alla rättigheter förbehållna.',
  accentColor: '#34D399'
};

// Email template editor component
const EmailTemplateEditor = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [components, setComponents] = useState<TemplateComponents>(defaultTemplateComponents);
  const [currentTemplate, setCurrentTemplate] = useState<string>(TEMPLATE_TYPES[0].id);
  const [templates, setTemplates] = useState<Record<string, string>>({});
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);
  const [debugMessage, setDebugMessage] = useState<string | null>(null);
  const { activeTab, setActiveTab } = useTabsContext();

  // Helper function to get readable status text
  const getStatusText = (status: string): string => {
    switch (status) {
      case 'pending': return 'tagits emot';
      case 'confirmed': return 'bekräftats';
      case 'preparing': return 'förbereds';
      case 'out_for_delivery': return 'är på väg';
      case 'delivered': return 'levererats';
      case 'cancelled': return 'avbrutits';
      case 'packed': return 'packats';
      default: return status;
    }
  };

  // Load templates from localStorage
  const loadTemplates = useCallback(() => {
    setIsLoading(true);
    
    try {
      const savedTemplates: Record<string, string> = {};
      let hasTemplates = false;
      
      // Try to load templates from localStorage
      for (const template of TEMPLATE_TYPES) {
        const saved = localStorage.getItem(`emailTemplate_${template.id}`);
        if (saved) {
          savedTemplates[template.id] = saved;
          hasTemplates = true;
        }
      }
      
      // If no templates were found, initialize with defaults
      if (!hasTemplates) {
        console.log('No templates found in localStorage, initializing defaults');
        for (const template of TEMPLATE_TYPES) {
          const defaultTemplate = JSON.stringify({
            ...defaultTemplateComponents,
            subject: `Din beställning har ${getStatusText(template.id)}`,
          });
          
          savedTemplates[template.id] = defaultTemplate;
          localStorage.setItem(`emailTemplate_${template.id}`, defaultTemplate);
        }
      }
      
      setTemplates(savedTemplates);
      
      // If no template is currently selected, select the first one
      if (!currentTemplate || !savedTemplates[currentTemplate]) {
        setCurrentTemplate(TEMPLATE_TYPES[0].id);
      }
    } catch (error) {
      console.error('Error loading templates:', error);
      setDebugMessage(`Error loading templates: ${error instanceof Error ? error.message : String(error)}`);
    } finally {
      setIsLoading(false);
    }
  }, [currentTemplate, setCurrentTemplate]);

  // Load templates on component mount
  useEffect(() => {
    try {
      console.log('Loading email templates on component mount...');
      loadTemplates();
    } catch (error) {
      console.error('Error in useEffect:', error);
      setDebugMessage(`Error initializing: ${error instanceof Error ? error.message : String(error)}`);
    }
  }, [loadTemplates]);

  // Handle template change selection
  const handleTemplateChange = (templateId: string) => {
    setCurrentTemplate(templateId);
    setSaveSuccess(false);
    
    // Load template components
    try {
      const savedTemplate = templates[templateId];
      if (savedTemplate) {
        // Parse the saved template
        const parsed = JSON.parse(savedTemplate);
        setComponents(parsed);
      } else {
        // Create a new template based on default
        const newTemplate = {
          ...defaultTemplateComponents,
          subject: `Din beställning har ${getStatusText(templateId)}`,
        };
        setComponents(newTemplate);
      }
      // Clear the preview HTML
      setPreviewHtml('');
    } catch (error) {
      console.error('Error loading template:', error);
      // If there was an error, reset to default
      setComponents(defaultTemplateComponents);
    }
  };

  const generateDefaultPreview = (templateId: string) => {
    try {
      // Use the built-in template function to generate the HTML
      const html = getOrderStatusEmailTemplate(SAMPLE_ORDER, templateId);
      setPreviewHtml(html || 'No preview available for this template');
    } catch (error) {
      console.error('Error generating preview:', error);
      setPreviewHtml('Error generating preview');
    }
  };

  const generatePreviewFromComponents = () => {
    try {
      // Generate HTML from components
      const html = generateHtmlFromComponents();
      setPreviewHtml(html);
    } catch (error) {
      console.error('Error generating preview:', error);
      setPreviewHtml('Error generating preview');
    }
  };

  // Generate HTML from the template components
  const generateHtmlFromComponents = (): string => {
    const orderItemsHtml = SAMPLE_ORDER.order_items.map(item => `
      <tr>
        <td style="padding: 8px; border-bottom: 1px solid #eee;">${item.product.name}</td>
        <td style="padding: 8px; border-bottom: 1px solid #eee; text-align: right;">${item.quantity} st</td>
        <td style="padding: 8px; border-bottom: 1px solid #eee; text-align: right;">${item.quantity * item.product.price} kr</td>
      </tr>
    `).join('');

    const orderDetailsSection = components.orderDetails ? `
      <div style="margin: 20px 0; padding: 15px; border: 1px solid #eee; border-radius: 4px; background-color: #f9f9f9;">
        <h3 style="margin-top: 0; color: #333; font-size: 16px;">Beställning #${SAMPLE_ORDER.id}</h3>
        <table style="width: 100%; border-collapse: collapse; margin-top: 10px;">
          <thead>
            <tr>
              <th style="text-align: left; padding: 8px; border-bottom: 2px solid #ddd;">Produkt</th>
              <th style="text-align: right; padding: 8px; border-bottom: 2px solid #ddd;">Antal</th>
              <th style="text-align: right; padding: 8px; border-bottom: 2px solid #ddd;">Pris</th>
            </tr>
          </thead>
          <tbody>
            ${orderItemsHtml}
          </tbody>
          <tfoot>
            <tr>
              <td colspan="2" style="text-align: right; padding: 8px; font-weight: bold;">Totalt:</td>
              <td style="text-align: right; padding: 8px; font-weight: bold;">${SAMPLE_ORDER.total_amount} kr</td>
            </tr>
          </tfoot>
        </table>
      </div>
    ` : '';

    const callToActionButton = components.callToAction ? `
      <div style="margin: 25px 0; text-align: center;">
        <a href="${components.callToActionUrl}" style="display: inline-block; padding: 10px 20px; background-color: ${components.accentColor}; color: white; text-decoration: none; border-radius: 4px; font-weight: bold;">
          ${components.callToAction}
        </a>
      </div>
    ` : '';

    return `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8">
          <title>${components.subject}</title>
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
        </head>
        <body style="font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif; line-height: 1.6; color: #333; max-width: 600px; margin: 0 auto; padding: 20px;">
          <div style="border: 1px solid #ddd; border-radius: 5px; padding: 20px; background-color: #fff;">
            <div style="text-align: center; margin-bottom: 20px; border-bottom: 3px solid ${components.accentColor}; padding-bottom: 20px;">
              <h1 style="color: #333; margin: 0; font-size: 24px;">Lokalaproducenter</h1>
            </div>
            
            <p style="font-size: 16px; margin-bottom: 10px;">${components.greeting}</p>
            
            <p style="margin-bottom: 15px;">${components.intro}</p>
            
            ${orderDetailsSection}
            
            <p style="margin: 15px 0;">${components.message}</p>
            
            ${callToActionButton}
            
            <p style="margin-top: 25px;">${components.signature}</p>
            
            <div style="margin-top: 30px; padding-top: 15px; border-top: 1px solid #eee; font-size: 12px; color: #777; text-align: center;">
              ${components.footerText}
            </div>
          </div>
        </body>
      </html>
    `;
  };

  const handleComponentChange = (key: keyof TemplateComponents, value: string | boolean) => {
    setComponents(prev => {
      const updated = {
        ...prev,
        [key]: value
      };
      
      // Generate preview with the updated components
      setTimeout(() => {
        try {
          const html = generateHtmlFromComponents();
          setPreviewHtml(html);
        } catch (e) {
          console.error('Error updating preview:', e);
        }
      }, 0);
      
      return updated;
    });
  };

  const handleSave = () => {
    setIsSaving(true);
    setSaveSuccess(false);
    
    try {
      // Save template components as JSON
      const templateJson = JSON.stringify(components);
      const success = saveTemplate(currentTemplate, templateJson);
      
      if (success) {
        // Update local state
        setTemplates({
          ...templates,
          [currentTemplate]: templateJson
        });
        
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 3000);
      } else {
        throw new Error('Failed to save template');
      }
    } catch (error) {
      console.error('Error saving template:', error);
      alert('Failed to save template');
    } finally {
      setIsSaving(false);
    }
  };

  const handleResetToDefault = () => {
    if (window.confirm('Are you sure you want to reset this template to default? Your changes will be lost.')) {
      try {
        // Delete using utility function
        const success = deleteTemplate(currentTemplate);
        
        if (success) {
          // Update local state
          const newTemplates = { ...templates };
          delete newTemplates[currentTemplate];
          setTemplates(newTemplates);
          
          // Reset components to default
          setComponents(defaultTemplateComponents);
          generateDefaultPreview(currentTemplate);
        } else {
          throw new Error('Failed to reset template');
        }
      } catch (err) {
        console.error('Error resetting template:', err);
        alert('Failed to reset template');
      }
    }
  };

  const handleGenerateContent = () => {
    // Set to default components for this template type
    setComponents(defaultTemplateComponents);
    generatePreviewFromComponents();
  };

  // Helper to automatically load default content if editor is empty
  useEffect(() => {
    if (!isLoading && currentTemplate) {
      // Auto-generate preview for empty state
      generatePreviewFromComponents();
    }
  }, [currentTemplate, isLoading]);

  return (
    <div className="container mx-auto p-4">
      <h1 className="text-2xl font-bold mb-4">E-postmallsredigerare</h1>
      
      {!isLoading && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <h2 className="text-lg font-semibold text-blue-800 mb-2">Hur du använder denna redigerare:</h2>
          <ol className="list-decimal ml-5 text-blue-700 space-y-1">
            <li>Välj en malltyp från sidofältet till vänster</li>
            <li>Anpassa mallsektionerna efter behov</li>
            <li>Använd förhandsgranskningsfliken för att se hur ditt e-postmeddelande kommer att se ut</li>
            <li>Klicka på "<span className="font-medium">Spara</span>" för att lagra din anpassade version</li>
          </ol>
          <p className="text-sm text-blue-600 mt-3">
            Mallar lagras i din webbläsare. Anpassade mallar kommer att visa en "Anpassad" märkning i sidofältet.
          </p>
        </div>
      )}
      
      {debugMessage && (
        <div className="mb-4 p-3 bg-yellow-50 text-yellow-700 rounded">
          <p className="font-semibold">Felsökningsinfo:</p>
          <p>{debugMessage}</p>
          <button
            onClick={() => {
              setDebugMessage(null);
              window.location.reload();
            }}
            className="mt-2 px-3 py-1 bg-yellow-600 text-white rounded hover:bg-yellow-700"
          >
            Försök igen
          </button>
        </div>
      )}
      
      {isLoading ? (
        <div className="text-center p-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-t-blue-500 border-r-transparent border-b-blue-500 border-l-transparent mb-3"></div>
          <p className="text-gray-600">Laddar mallar...</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 mb-6">
          <div className="col-span-1 bg-white p-4 rounded shadow">
            <h2 className="text-lg font-semibold mb-3">Malltyper</h2>
            <ul className="space-y-2">
              {TEMPLATE_TYPES.map(template => (
                <li 
                  key={template.id}
                  className={`p-3 rounded cursor-pointer border transition-all ${currentTemplate === template.id 
                    ? 'bg-blue-50 border-blue-300 shadow-sm' 
                    : 'border-gray-100 hover:bg-gray-50 hover:border-gray-200'}`}
                  onClick={() => handleTemplateChange(template.id)}
                >
                  <div className="font-medium text-gray-800">{template.name}</div>
                  <div className="text-sm text-gray-600">{template.description}</div>
                  
                  {templates[template.id] && (
                    <div className="flex items-center gap-2 mt-1.5">
                      <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">
                        <CheckCircle size={12} className="mr-1" />
                        Anpassad
                      </span>
                      
                      {(() => {
                        try {
                          const savedTemplate = JSON.parse(templates[template.id]);
                          const isEnabled = savedTemplate?.enabled !== false; // Default to true if not specified
                          
                          return (
                            <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                              isEnabled 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-red-100 text-red-800'
                            }`}>
                              {isEnabled ? 'Aktiv' : 'Inaktiv'}
                            </span>
                          );
                        } catch (e) {
                          return null;
                        }
                      })()}
                    </div>
                  )}
                  
                </li>
              ))}
            </ul>
            
            <div className="mt-6 pt-4 border-t border-gray-100">
              <h3 className="text-sm font-medium text-gray-700 mb-2">Tips:</h3>
              <ul className="text-xs text-gray-600 space-y-1">
                <li>• Du kan använda <code className="bg-gray-100 px-1 rounded">{"{{customer_name}}"}</code> i textfält</li>
                <li>• Förhandsgranskning visar hur e-post kommer att se ut för kunder</li>
                <li>• Klicka på "Återställ" för att återställa standardinställningar</li>
              </ul>
            </div>
          </div>
          
          <div className="lg:col-span-3">
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="border-b border-gray-200">
                <nav className="flex -mb-px">
                  <button
                    onClick={() => setActiveTab('editor')}
                    className={`px-6 py-3 font-medium text-sm ${
                      activeTab === 'editor'
                        ? 'border-b-2 border-blue-500 text-blue-600'
                        : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    Redigerare
                  </button>
                  <button
                    onClick={() => setActiveTab('preview')}
                    className={`px-6 py-3 font-medium text-sm ${
                      activeTab === 'preview'
                        ? 'border-b-2 border-blue-500 text-blue-600'
                        : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    Förhandsgranskning
                  </button>
                </nav>
              </div>
              
              {activeTab === 'editor' && (
                <div className="p-4">
                  <div className="flex justify-between items-center mb-4">
                    <h2 className="text-lg font-semibold text-gray-800">
                      {TEMPLATE_TYPES.find(t => t.id === currentTemplate)?.name} Mall
                    </h2>
                    <div className="flex gap-2">
                      <button
                        onClick={handleResetToDefault}
                        className="px-3 py-1.5 text-sm text-gray-600 border border-gray-300 rounded hover:bg-gray-50 flex items-center gap-1"
                        title="Återställ till standardmall"
                      >
                        <RefreshCw size={14} />
                        Återställ
                      </button>
                      <button
                        onClick={handleGenerateContent}
                        className="px-3 py-1.5 text-sm bg-blue-50 text-blue-700 border border-blue-200 rounded hover:bg-blue-100 flex items-center gap-1"
                        title="Ladda standardinnehåll"
                      >
                        <Eye size={14} />
                        Standardinnehåll
                      </button>
                      <button
                        onClick={handleSave}
                        disabled={isSaving}
                        className={`px-3 py-1.5 text-sm text-white rounded flex items-center gap-1 ${
                          saveSuccess 
                            ? 'bg-green-600' 
                            : isSaving
                              ? 'bg-gray-400 cursor-not-allowed'
                              : 'bg-blue-600 hover:bg-blue-700'
                        }`}
                      >
                        {saveSuccess ? <CheckCircle size={14} /> : <Save size={14} />}
                        {isSaving ? 'Sparar...' : (saveSuccess ? 'Sparat!' : 'Spara')}
                      </button>
                    </div>
                  </div>
                  
                  {/* Enable/Disable Template */}
                  <div className="border rounded-lg p-4 bg-gray-50 mb-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className={`w-3 h-3 rounded-full ${components.enabled ? 'bg-green-500' : 'bg-red-500'}`}></div>
                        <h3 className="font-medium text-gray-800">Status: {components.enabled ? 'Aktiverad' : 'Inaktiverad'}</h3>
                      </div>
                      <label className="flex items-center cursor-pointer">
                        <span className="mr-2 text-sm">{components.enabled ? 'E-post skickas' : 'E-post skickas inte'}</span>
                        <div className="relative">
                          <input
                            type="checkbox"
                            checked={components.enabled}
                            onChange={(e) => handleComponentChange('enabled', e.target.checked)}
                            className="sr-only"
                          />
                          <div className={`block w-10 h-6 rounded-full transition-colors ${components.enabled ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                          <div className={`absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition transform ${components.enabled ? 'translate-x-4' : ''}`}></div>
                        </div>
                      </label>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      {components.enabled 
                        ? 'När denna mall är aktiverad kommer e-post att skickas till kunden när en beställning når denna status.' 
                        : 'När denna mall är inaktiverad kommer inga e-postmeddelanden att skickas för denna orderstatus.'}
                    </p>
                  </div>
                  
                  <div className="space-y-4 max-h-[calc(100vh-300px)] overflow-y-auto p-1">
                    {/* Email Subject */}
                    <div className="border rounded-lg p-4 bg-gray-50">
                      <div className="flex items-center gap-2 mb-2">
                        <Layout size={18} className="text-gray-500" />
                        <h3 className="font-medium text-gray-800">E-postämne</h3>
                      </div>
                      <input
                        type="text"
                        value={components.subject}
                        onChange={(e) => handleComponentChange('subject', e.target.value)}
                        className="w-full p-2 border rounded"
                        placeholder="E-postens ämnesrad"
                      />
                    </div>
                    
                    {/* Greeting */}
                    <div className="border rounded-lg p-4 bg-gray-50">
                      <div className="flex items-center gap-2 mb-2">
                        <Type size={18} className="text-gray-500" />
                        <h3 className="font-medium text-gray-800">Hälsning</h3>
                      </div>
                      <input
                        type="text"
                        value={components.greeting}
                        onChange={(e) => handleComponentChange('greeting', e.target.value)}
                        className="w-full p-2 border rounded"
                        placeholder={"Hej {{customer_name}},"}
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        You can use variables like {'{{customer_name}}'} in your subject.
                      </p>
                    </div>
                    
                    {/* Introduction */}
                    <div className="border rounded-lg p-4 bg-gray-50">
                      <div className="flex items-center gap-2 mb-2">
                        <Type size={18} className="text-gray-500" />
                        <h3 className="font-medium text-gray-800">Introduktion</h3>
                      </div>
                      <textarea
                        value={components.intro}
                        onChange={(e) => handleComponentChange('intro', e.target.value)}
                        className="w-full p-2 border rounded"
                        rows={2}
                        placeholder="Kort introduktion om detta e-postmeddelande"
                      />
                    </div>
                    
                    {/* Order Details */}
                    <div className="border rounded-lg p-4 bg-gray-50">
                      <div className="flex items-center gap-2 mb-2">
                        <List size={18} className="text-gray-500" />
                        <h3 className="font-medium text-gray-800">Beställningsdetaljer</h3>
                      </div>
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={components.orderDetails}
                          onChange={(e) => handleComponentChange('orderDetails', e.target.checked)}
                          className="mr-2 h-4 w-4"
                        />
                        <span>Inkludera tabell med beställningsdetaljer i e-post</span>
                      </label>
                    </div>
                    
                    {/* Main Message */}
                    <div className="border rounded-lg p-4 bg-gray-50">
                      <div className="flex items-center gap-2 mb-2">
                        <Type size={18} className="text-gray-500" />
                        <h3 className="font-medium text-gray-800">Huvudmeddelande</h3>
                      </div>
                      <textarea
                        value={components.message}
                        onChange={(e) => handleComponentChange('message', e.target.value)}
                        className="w-full p-2 border rounded"
                        rows={4}
                        placeholder="E-postens huvudsakliga meddelande"
                      />
                      <p className="text-xs text-gray-500 mt-1">Huvudinnehållet i ditt e-postmeddelande</p>
                    </div>
                    
                    {/* Call to Action */}
                    <div className="border rounded-lg p-4 bg-gray-50">
                      <div className="flex items-center gap-2 mb-2">
                        <Image size={18} className="text-gray-500" />
                        <h3 className="font-medium text-gray-800">Handlingsknapp</h3>
                      </div>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={components.callToAction}
                          onChange={(e) => handleComponentChange('callToAction', e.target.value)}
                          className="flex-1 p-2 border rounded"
                          placeholder="Knapptext"
                        />
                        <input
                          type="text"
                          value={components.callToActionUrl}
                          onChange={(e) => handleComponentChange('callToActionUrl', e.target.value)}
                          className="flex-1 p-2 border rounded"
                          placeholder="Knapp-URL"
                        />
                      </div>
                      <p className="text-xs text-gray-500 mt-1">Lämna knapptexten tom för att ta bort denna sektion</p>
                    </div>
                    
                    {/* Signature */}
                    <div className="border rounded-lg p-4 bg-gray-50">
                      <div className="flex items-center gap-2 mb-2">
                        <Type size={18} className="text-gray-500" />
                        <h3 className="font-medium text-gray-800">Signatur</h3>
                      </div>
                      <input
                        type="text"
                        value={components.signature}
                        onChange={(e) => handleComponentChange('signature', e.target.value)}
                        className="w-full p-2 border rounded"
                        placeholder="E-postsignatur"
                      />
                    </div>
                    
                    {/* Footer Text */}
                    <div className="border rounded-lg p-4 bg-gray-50">
                      <div className="flex items-center gap-2 mb-2">
                        <Type size={18} className="text-gray-500" />
                        <h3 className="font-medium text-gray-800">Sidfotstext</h3>
                      </div>
                      <input
                        type="text"
                        value={components.footerText}
                        onChange={(e) => handleComponentChange('footerText', e.target.value)}
                        className="w-full p-2 border rounded"
                        placeholder="Sidfotstext"
                      />
                    </div>
                    
                    {/* Accent Color */}
                    <div className="border rounded-lg p-4 bg-gray-50">
                      <div className="flex items-center gap-2 mb-2">
                        <Layout size={18} className="text-gray-500" />
                        <h3 className="font-medium text-gray-800">Accentfärg</h3>
                      </div>
                      <div className="flex items-center gap-2">
                        <input
                          type="color"
                          value={components.accentColor}
                          onChange={(e) => handleComponentChange('accentColor', e.target.value)}
                          className="w-12 h-8 border rounded cursor-pointer"
                        />
                        <input
                          type="text"
                          value={components.accentColor}
                          onChange={(e) => handleComponentChange('accentColor', e.target.value)}
                          className="w-32 p-2 border rounded"
                          placeholder="#34D399"
                        />
                        <span className="text-sm text-gray-600">Färg för rubriker och knappar</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              {activeTab === 'preview' && (
                <div>
                  <div className="border-b p-4 bg-gray-50">
                    <h2 className="text-lg font-semibold text-gray-800">
                      {TEMPLATE_TYPES.find(t => t.id === currentTemplate)?.name} Förhandsgranskning
                    </h2>
                    <p className="text-sm text-gray-600 mt-1">
                      Så här kommer ditt e-postmeddelande att visas för kunder.
                    </p>
                    
                    {!components.enabled && (
                      <div className="mt-3 p-2 bg-red-50 text-red-700 border border-red-200 rounded-md flex items-center">
                        <div className="w-2 h-2 bg-red-500 rounded-full mr-2"></div>
                        <span className="text-sm font-medium">Denna mall är inaktiverad och inga e-postmeddelanden kommer att skickas.</span>
                      </div>
                    )}
                  </div>
                  
                  {!previewHtml ? (
                    <div className="p-8 text-center">
                      <p className="text-gray-500">Ingen förhandsgranskning tillgänglig. Återgå till redigeringsfliken och konfigurera din mall.</p>
                    </div>
                  ) : (
                    <div className="p-4 overflow-auto" style={{ maxHeight: 'calc(100vh - 250px)' }}>
                      {!components.enabled && (
                        <div className="relative">
                          <div className="absolute inset-0 bg-gray-200 bg-opacity-50 flex items-center justify-center z-10">
                            <div className="bg-white p-4 rounded-lg border border-red-300 shadow-lg">
                              <h3 className="text-lg font-bold text-red-600 mb-2">E-postmall inaktiverad</h3>
                              <p className="text-gray-700">Denna mall kommer inte att skickas till kunder.</p>
                            </div>
                          </div>
                          <iframe
                            srcDoc={previewHtml}
                            className="w-full h-[calc(100vh-300px)] min-h-[500px] border rounded"
                            title="E-postförhandsgranskning"
                          />
                        </div>
                      )}
                      
                      {components.enabled && (
                        <iframe
                          srcDoc={previewHtml}
                          className="w-full h-[calc(100vh-300px)] min-h-[500px] border rounded"
                          title="E-postförhandsgranskning"
                        />
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Template interface matching the database structure
interface EmailTemplate {
  id: string;
  subject_template: string;
  body_template: string;
  created_at?: string;
  updated_at?: string;
  is_active: boolean;
}

// Default empty template
const emptyTemplate: EmailTemplate = {
  id: '',
  subject_template: '',
  body_template: '',
  is_active: true
};

// Available order statuses
const ORDER_STATUSES = [
  { value: 'pending', label: 'Pending' },
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'preparing', label: 'Preparing' },
  { value: 'out_for_delivery', label: 'Out for Delivery' },
  { value: 'delivered', label: 'Delivered' },
  { value: 'cancelled', label: 'Cancelled' }
];

// Add this interface and component just before the default export function
// Template interface for email_template_versions table
interface EmailTemplateVersion {
  id: string;
  template_id: string;
  subject: string;
  body: string;
  created_at: string;
  created_by: string;
}

// Simple editor for order confirmation template
function OrderConfirmationEditor() {
  const [template, setTemplate] = useState<EmailTemplateVersion | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);

  // Load template on mount
  useEffect(() => {
    loadTemplate();
  }, []);

  const loadTemplate = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('email_template_versions')
        .select('*')
        .eq('template_id', 'order_confirmation')
        .order('created_at', { ascending: false })
        .limit(1);

      if (error) throw error;
      
      if (data && data.length > 0) {
        setTemplate(data[0]);
      } else {
        // Default template if none exists
        setTemplate({
          id: '',
          template_id: 'order_confirmation',
          subject: 'Orderbekräftelse #{{order_number}}',
          body: 'Tack för din beställning',
          created_at: new Date().toISOString(),
          created_by: 'admin'
        });
      }
    } catch (error) {
      console.error('Error loading template:', error);
      toast.error('Failed to load template');
    } finally {
      setIsLoading(false);
    }
  };

  // Save template
  const saveTemplate = async () => {
    if (!template) return;
    
    setIsSaving(true);
    try {
      const { data, error } = await supabase
        .from('email_template_versions')
        .insert({
          template_id: 'order_confirmation',
          subject: template.subject,
          body: template.body,
          created_by: 'admin'
        });

      if (error) throw error;
      
      toast.success('Template saved successfully');
      loadTemplate(); // Reload to get the new version
    } catch (error) {
      console.error('Error saving template:', error);
      toast.error('Failed to save template');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (!template) {
    return <div>No template found</div>;
  }

  return (
    <div className="p-4 bg-white rounded-lg shadow">
      <h2 className="text-xl font-bold mb-4">Order Confirmation Email Template</h2>
      
      <div className="mb-4">
        <label className="block mb-2">Subject:</label>
        <input
          type="text"
          value={template.subject}
          onChange={(e) => setTemplate({...template, subject: e.target.value})}
          className="w-full p-2 border rounded"
        />
        <p className="text-sm text-gray-500 mt-1">
          Use {"{"}{"{"}"order_number"{"}"}{"}"} to include the order number
        </p>
      </div>
      
      <div className="mb-4">
        <label className="block mb-2">Body:</label>
        <textarea
          value={template.body}
          onChange={(e) => setTemplate({...template, body: e.target.value})}
          className="w-full p-2 border rounded h-64 font-mono"
        />
        <p className="text-sm text-gray-500 mt-1">
          Available variables: {"{"}{"{"}"order_number"{"}"}{"}"},
          {"{"}{"{"}"customer_name"{"}"}{"}"},
          {"{"}{"{"}"total_amount"{"}"}{"}"},
          {"{"}{"{"}"delivery_date"{"}"}{"}"},
          {"{"}{"{"}"delivery_time"{"}"}{"}"},
          {"{"}{"{"}"delivery_address"{"}"}{"}"}, etc.
        </p>
      </div>
      
      <div className="flex justify-end">
        <button
          onClick={saveTemplate}
          disabled={isSaving}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          {isSaving ? 'Saving...' : 'Save Template'}
        </button>
      </div>
    </div>
  );
}

// Modify the default export to include tabs for selecting between templates
export default function EmailTemplateAdmin() {
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [currentTemplate, setCurrentTemplate] = useState<EmailTemplate>(emptyTemplate);
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);
  // Add state for active tab
  const [activeTab, setActiveTab] = useState<'status' | 'confirmation'>('status');

  // Load templates on component mount
  useEffect(() => {
    loadTemplates();
  }, []);

  // Load templates from database
  const loadTemplates = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('email_templates')
        .select('*')
        .order('id');

      if (error) throw error;
      
      setTemplates(data || []);
    } catch (error) {
      console.error('Error loading templates:', error);
      toast.error('Failed to load email templates');
    } finally {
      setIsLoading(false);
    }
  };

  // Save template
  const saveTemplate = async () => {
    if (!currentTemplate.id || !currentTemplate.subject_template) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsLoading(true);
    try {
      const { error } = await supabase
        .from('email_templates')
        .upsert({
          id: currentTemplate.id,
          subject_template: currentTemplate.subject_template,
          body_template: currentTemplate.body_template,
          is_active: currentTemplate.is_active,
          updated_at: new Date().toISOString()
        }, { onConflict: 'id' });

      if (error) throw error;
      
      toast.success('Template saved successfully');
      setIsEditing(false);
      setCurrentTemplate(emptyTemplate);
      loadTemplates();
    } catch (error) {
      console.error('Error saving template:', error);
      toast.error('Failed to save template');
    } finally {
      setIsLoading(false);
    }
  };

  // Delete template
  const deleteTemplate = async (id: string) => {
    if (!confirm('Are you sure you want to delete this template?')) return;

    setIsLoading(true);
    try {
      const { error } = await supabase
        .from('email_templates')
        .delete()
        .eq('id', id);

      if (error) throw error;
      
      toast.success('Template deleted successfully');
      loadTemplates();
    } catch (error) {
      console.error('Error deleting template:', error);
      toast.error('Failed to delete template');
    } finally {
      setIsLoading(false);
    }
  };

  // Start editing a template
  const editTemplate = (template: EmailTemplate) => {
    setCurrentTemplate(template);
    setIsEditing(true);
  };

  // Cancel editing
  const cancelEdit = () => {
    setCurrentTemplate(emptyTemplate);
    setIsEditing(false);
  };

  // Generate preview of template
  const generatePreview = (template: EmailTemplate) => {
    // Sample order data for preview
    const SAMPLE_ORDER = {
      id: '12345-exampleorder',
      order_number: '12345',
      created_at: new Date().toISOString(),
      status: template.id,
      total_amount: 1250,
      delivery_date: new Date().toISOString().split('T')[0],
      delivery_time: '14:00-16:00',
      delivery_notes: 'Please leave at door',
      delivery_address: 'Main Street 1, 123 45 Stockholm',
      guest_email: 'customer@example.com',
      guest_name: 'Test Customer',
      guest_details: {
        firstName: 'Test',
        lastName: 'Customer'
      },
      profile: {
        id: 'profile-123',
        first_name: 'Test',
        last_name: 'Customer',
        email: 'customer@example.com',
        full_name: 'Test Customer'
      },
      order_items: [
        {
          id: 'item-1',
          product: {
            id: 'product-1',
            name: 'Organic apples',
            price: 25,
          },
          quantity: 4,
          unit_price: 25
        },
        {
          id: 'item-2',
          product: {
            id: 'product-2',
            name: 'Local honey',
            price: 89,
          },
          quantity: 1,
          unit_price: 89
        }
      ]
    };

    // Process template variables
    let html = template.body_template;
    let subject = template.subject_template;
    
    // Replace basic variables
    const replacements: Record<string, string> = {
      '{{order_number}}': SAMPLE_ORDER.order_number,
      '{{customer_name}}': SAMPLE_ORDER.guest_name,
      '{{total_amount}}': SAMPLE_ORDER.total_amount.toString(),
      '{{delivery_date}}': SAMPLE_ORDER.delivery_date,
      '{{delivery_time}}': SAMPLE_ORDER.delivery_time,
      '{{delivery_address}}': SAMPLE_ORDER.delivery_address,
      '{{delivery_notes}}': SAMPLE_ORDER.delivery_notes
    };
    
    // Apply replacements
    Object.entries(replacements).forEach(([key, value]) => {
      html = html.replace(new RegExp(key, 'g'), value);
      subject = subject.replace(new RegExp(key, 'g'), value);
    });
    
    setPreviewHtml(html);
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-3xl font-semibold mb-6">Email Templates</h1>
      
      <div className="mb-6 bg-white p-4 rounded-lg border border-gray-200">
        <p className="text-gray-700">
          Email templates are used when sending order status updates and confirmations to customers.
        </p>
      </div>

      {/* Tab navigation */}
      <div className="mb-6 border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('status')}
            className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'status'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Status Email Templates
          </button>
          <button
            onClick={() => setActiveTab('confirmation')}
            className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'confirmation'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Order Confirmation Template
          </button>
        </nav>
      </div>

      {/* Content based on active tab */}
      {activeTab === 'status' ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Templates List */}
          <div className="md:col-span-1">
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
              <div className="p-4 border-b border-gray-200 bg-gray-50">
                <h2 className="text-xl font-medium text-gray-800">Templates</h2>
              </div>
              
              <div className="p-4">
                {isLoading && (
                  <div className="flex items-center justify-center p-6">
                    <div className="inline-block h-6 w-6 animate-spin rounded-full border-4 border-solid border-blue-600 border-r-transparent"></div>
                    <span className="ml-3 text-gray-600">Loading templates...</span>
                  </div>
                )}
                
                {!isLoading && templates.length === 0 && (
                  <div className="text-center py-6">
                    <p className="text-gray-500 mb-4">No templates found</p>
                    <button 
                      onClick={() => {
                        setCurrentTemplate(emptyTemplate);
                        setIsEditing(true);
                      }}
                      className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                    >
                      Create Your First Template
                    </button>
                  </div>
                )}
                
                {!isLoading && templates.length > 0 && (
                  <>
                    <ul className="divide-y divide-gray-200">
                      {templates.map(template => (
                        <li 
                          key={template.id} 
                          className="py-3 hover:bg-gray-50 transition-colors"
                        >
                          <div className="flex justify-between items-center">
                            <div className="flex-1">
                              <p className="font-medium text-gray-900">{template.id}</p>
                              <div className="flex gap-2 mt-1">
                                <span className={`px-2 py-0.5 text-xs rounded-full ${
                                  template.is_active 
                                    ? 'bg-green-100 text-green-800' 
                                    : 'bg-red-100 text-red-800'
                                }`}>
                                  {template.is_active ? 'Active' : 'Inactive'}
                                </span>
                              </div>
                            </div>
                            <div className="flex space-x-1">
                              <button 
                                onClick={() => generatePreview(template)} 
                                className="p-1.5 text-sm text-green-600 hover:bg-green-50 hover:text-green-800 rounded transition-colors"
                                title="Preview template"
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                  <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                                  <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
                                </svg>
                              </button>
                              <button 
                                onClick={() => editTemplate(template)} 
                                className="p-1.5 text-sm text-blue-600 hover:bg-blue-50 hover:text-blue-800 rounded transition-colors"
                                title="Edit template"
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                  <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                                </svg>
                              </button>
                              <button 
                                onClick={() => deleteTemplate(template.id)} 
                                className="p-1.5 text-sm text-red-600 hover:bg-red-50 hover:text-red-800 rounded transition-colors"
                                title="Delete template"
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                  <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                                </svg>
                              </button>
                            </div>
                          </div>
                        </li>
                      ))}
                    </ul>
                    
                    <button 
                      onClick={() => {
                        setCurrentTemplate(emptyTemplate);
                        setIsEditing(true);
                      }}
                      className="w-full mt-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center justify-center"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                      </svg>
                      Add New Template
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
          
          {/* Template Editor/Preview Column */}
          <div className="md:col-span-2">
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
              {/* Keep your existing editor/preview code */}
              {isEditing ? (
                // Your existing editor UI
                <div>
                  <div className="p-4 border-b border-gray-200 bg-gray-50 flex justify-between items-center">
                    <h2 className="text-xl font-medium text-gray-800">
                      {currentTemplate.id ? `Edit Template: ${currentTemplate.id}` : 'Create New Template'}
                    </h2>
                    <button 
                      onClick={cancelEdit}
                      className="text-gray-500 hover:text-gray-700 transition-colors"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </div>
                  
                  <div className="p-5">
                    <form onSubmit={(e) => { e.preventDefault(); saveTemplate(); }} className="space-y-5">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Template ID (Status)</label>
                        <select 
                          value={currentTemplate.id} 
                          onChange={(e) => setCurrentTemplate({...currentTemplate, id: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                          required
                        >
                          <option value="">Select a status</option>
                          {ORDER_STATUSES.map(status => (
                            <option key={status.value} value={status.value}>
                              {status.label}
                            </option>
                          ))}
                          <option value="order_confirmation">Order Confirmation</option>
                        </select>
                        <p className="text-xs text-gray-500 mt-1">
                          This should match the order status this template will be used for.
                        </p>
                      </div>
                      
                      <div>
                        <div className="flex items-center mb-1">
                          <input 
                            type="checkbox" 
                            id="is_active"
                            checked={currentTemplate.is_active} 
                            onChange={(e) => setCurrentTemplate({...currentTemplate, is_active: e.target.checked})}
                            className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <label htmlFor="is_active" className="ml-2 block text-sm font-medium text-gray-700">
                            Template is active
                          </label>
                        </div>
                        <p className="text-xs text-gray-500">
                          {currentTemplate.is_active 
                            ? 'This template will be used when sending emails.' 
                            : 'This template is inactive and will not be used.'}
                        </p>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Subject Template</label>
                        <input 
                          type="text" 
                          value={currentTemplate.subject_template} 
                          onChange={(e) => setCurrentTemplate({...currentTemplate, subject_template: e.target.value})}
                          placeholder="Email subject line"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                          required
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          You can use variables like {'{{order_number}}'} in your subject.
                        </p>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Body Template</label>
                        <textarea 
                          value={currentTemplate.body_template} 
                          onChange={(e) => setCurrentTemplate({...currentTemplate, body_template: e.target.value})}
                          placeholder="Email body content (HTML supported)"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
                          rows={12}
                          required
                        />
                        <div className="mt-2 text-xs text-gray-500">
                          <p className="font-medium mb-1">Available variables:</p>
                          <div className="flex flex-wrap gap-2">
                            <code className="px-1.5 py-0.5 bg-gray-100 rounded">{'{{order_number}}'}</code>
                            <code className="px-1.5 py-0.5 bg-gray-100 rounded">{'{{customer_name}}'}</code>
                            <code className="px-1.5 py-0.5 bg-gray-100 rounded">{'{{total_amount}}'}</code>
                            <code className="px-1.5 py-0.5 bg-gray-100 rounded">{'{{delivery_date}}'}</code>
                            <code className="px-1.5 py-0.5 bg-gray-100 rounded">{'{{delivery_time}}'}</code>
                            <code className="px-1.5 py-0.5 bg-gray-100 rounded">{'{{delivery_address}}'}</code>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex justify-end gap-3 pt-3 border-t border-gray-100">
                        <button 
                          type="button"
                          onClick={cancelEdit}
                          className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                        >
                          Cancel
                        </button>
                        <button 
                          type="submit"
                          className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors flex items-center"
                          disabled={isLoading}
                        >
                          {isLoading ? (
                            <>
                              <div className="h-4 w-4 mr-2 animate-spin rounded-full border-2 border-solid border-white border-r-transparent"></div>
                              Saving...
                            </>
                          ) : 'Save Template'}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              ) : previewHtml ? (
                // Your existing preview UI
                <div>
                  <div className="p-4 border-b border-gray-200 bg-gray-50 flex justify-between items-center">
                    <h2 className="text-xl font-medium text-gray-800">Email Preview</h2>
                    <button 
                      onClick={() => setPreviewHtml(null)}
                      className="text-gray-500 hover:text-gray-700 transition-colors"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </div>
                  
                  <div className="p-4">
                    <iframe 
                      srcDoc={previewHtml}
                      className="w-full h-[500px] border rounded"
                      title="Email Preview"
                    />
                  </div>
                </div>
              ) : (
                // Empty state
                <div className="flex flex-col items-center justify-center p-10 h-80">
                  <div className="text-center max-w-md">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">Email Template Management</h3>
                    <p className="text-gray-500 mb-6">
                      Select a template to edit or preview, or create a new template to customize your order status notifications.
                    </p>
                    <button 
                      onClick={() => {
                        setCurrentTemplate(emptyTemplate);
                        setIsEditing(true);
                      }}
                      className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center justify-center mx-auto"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                      </svg>
                      Create New Template
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      ) : (
        // Order confirmation template editor tab
        <OrderConfirmationEditor />
      )}
    </div>
  );
} 